import React, { TouchEvent, useCallback, useEffect, useRef } from 'react'
import cloneDeep from 'lodash.clonedeep'
import { useTimeout } from './use-timeout'

const DEFAULT_THRESHOLD = 250

export function useLongPress(
  onStart: () => void,
  onEnd?: () => void,
  onClick?: (e: React.TouchEvent | React.MouseEvent) => void,
  onlyOnTouchDevices = true,
) {
  const isLongPressActive = useRef(false)
  const isPressed = useRef(false)
  const { create: createTimeout, clear: cancelTimeout } = useTimeout()

  const touchEventRef = useRef<TouchEvent>()

  const start = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const canProcessEvent =
        ('button' in e && e.button === 0) ||
        ('touches' in e && e.touches.length > 0)
      if (isPressed.current || !canProcessEvent) {
        return
      }

      if ('touches' in e && e.touches.length) {
        touchEventRef.current = cloneDeep(e)
      }

      isPressed.current = true
      createTimeout(() => {
        onStart()
        isLongPressActive.current = true
      }, DEFAULT_THRESHOLD)
    },
    [onStart, createTimeout],
  )

  const cancelIfIsNotActive = useCallback(() => {
    if (!isLongPressActive.current) {
      cancelTimeout()
      isPressed.current = false
    }
  }, [cancelTimeout])

  const cancel = useCallback(() => {
    if (isLongPressActive.current) {
      onEnd?.()
    }

    isLongPressActive.current = false
    isPressed.current = false
    cancelTimeout()
  }, [onEnd, cancelTimeout])

  const cancelWithClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      try {
        if (!isLongPressActive.current && isPressed.current) {
          isPressed.current = false
          if (onClick) e.preventDefault()
          onClick?.(touchEventRef.current || e)
          touchEventRef.current = undefined
        }
      } catch (e) {
        //
      }

      cancel()
    },
    [onClick, cancel],
  )

  if (onlyOnTouchDevices)
    return {
      onTouchStart: start,
      onTouchEnd: cancelWithClick,
      onTouchMove: cancelIfIsNotActive,
    }

  return {
    onMouseDown: start,
    onMouseUp: cancelWithClick,
    onmousemove: cancelIfIsNotActive,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchEnd: cancelWithClick,
    onTouchMove: cancelIfIsNotActive,
  }
}
